import moment from 'moment-timezone';

/**
 * Shared door sensor utilities for pulse delta calculations.
 * Single source of truth for all door open count computations.
 */

/** Label overrides for sensor types whose auto-generated name is wrong. */
const SENSOR_TYPE_LABELS = {
    door_status: 'Counter',
};

/**
 * Return a human-readable label for a sensor type key.
 * Uses SENSOR_TYPE_LABELS when an override exists, otherwise title-cases the key.
 */
export const getSensorTypeLabel = (sensorType) =>
    SENSOR_TYPE_LABELS[sensorType] ??
    sensorType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

/**
 * Extract sorted entries for an arbitrary cumulative metric from timeline data.
 * Generalises extractPulseEntries so the same delta/aggregation pipeline can be
 * reused for any monotonically-increasing reading (door pulses, meter readings…).
 * @param {Array} timelines - sensor.timelines array
 * @param {string} metricKey - timeline metric key (e.g. 'total_pulses', 'meter_reading')
 * @returns {Array} sorted array of { time: string, momentTime: Moment, value: number }
 */
export const extractMetricEntries = (timelines, metricKey) => {
    if (!timelines || !timelines.length || !metricKey) return [];

    return timelines
        // Explicit presence check: keep timelines that HAVE the metric slot.
        // `!= null` (not a truthy check) so a slot is never dropped on account
        // of its value — the slot is an object {value,time,unit} anyway, but
        // this is unambiguous about intent (key present vs absent).
        .filter(t => t[metricKey] != null)
        .map(t => ({
            time: t[metricKey].time,
            momentTime: moment(t[metricKey].time, 'DD-MM-YYYY HH:mm'),
            value: parseFloat(t[metricKey].value) || 0
        }))
        .filter(t => t.momentTime.isValid())
        .sort((a, b) => a.momentTime.valueOf() - b.momentTime.valueOf());
};

/**
 * Extract sorted pulse entries from timeline data (door sensor — total_pulses).
 * @param {Array} timelines - sensor.timelines array
 * @returns {Array} sorted array of { time: string, momentTime: Moment, value: number }
 */
export const extractPulseEntries = (timelines) =>
    extractMetricEntries(timelines, 'total_pulses');

/**
 * Calculate total delta (last - first) for a set of pulse entries.
 * Used for summary counts (today's opens, etc.)
 * @param {Array} pulseEntries - sorted array from extractPulseEntries
 * @returns {number} total opens count
 */
export const calculateTotalDelta = (pulseEntries) => {
    if (pulseEntries.length < 2) return 0;
    return Math.max(0, Math.round(
        pulseEntries[pulseEntries.length - 1].value - pulseEntries[0].value
    ));
};

/**
 * Calculate per-reading deltas between consecutive pulse entries.
 * @param {Array} pulseEntries - sorted array from extractPulseEntries
 * @returns {Array} array of { time: string, value: number }
 */
export const calculatePerReadingDeltas = (pulseEntries) => {
    if (pulseEntries.length < 2) return [];

    const deltas = [];
    for (let i = 1; i < pulseEntries.length; i++) {
        const delta = Math.max(0, pulseEntries[i].value - pulseEntries[i - 1].value);
        deltas.push({ time: pulseEntries[i].time, value: delta });
    }
    return deltas;
};

/**
 * Aggregate per-reading deltas into time buckets.
 * @param {Array} deltaData - array from calculatePerReadingDeltas
 * @param {string} interval - '15min' | '1hour' | 'daily'
 * @returns {Array} sorted array of { time: string, value: number }
 */
export const aggregateIntoBuckets = (deltaData, interval) => {
    if (!deltaData.length) return [];

    const buckets = {};
    deltaData.forEach(d => {
        const m = moment(d.time, 'DD-MM-YYYY HH:mm');
        if (!m.isValid()) return;

        let bucketKey;
        if (interval === '15min') {
            const minute = Math.floor(m.minute() / 15) * 15;
            bucketKey = m.clone().minute(minute).second(0).format('DD-MM-YYYY HH:mm');
        } else if (interval === '1hour') {
            bucketKey = m.clone().minute(0).second(0).format('DD-MM-YYYY HH:mm');
        } else if (interval === 'daily') {
            bucketKey = m.clone().startOf('day').format('DD-MM-YYYY');
        }

        if (!buckets[bucketKey]) {
            buckets[bucketKey] = { time: bucketKey, value: 0 };
        }
        buckets[bucketKey].value += d.value;
    });

    const format = interval === 'daily' ? 'DD-MM-YYYY' : 'DD-MM-YYYY HH:mm';
    return Object.values(buckets).sort((a, b) =>
        moment(a.time, format).valueOf() - moment(b.time, format).valueOf()
    );
};

/**
 * Aggregate raw metric entries into time buckets, keeping the LATEST value in
 * each bucket (not a sum). Use this for level/cumulative metrics like a meter
 * reading, where each bucket should show the reading as of the end of that
 * interval — as opposed to aggregateIntoBuckets which sums deltas (counts).
 * @param {Array} entries - array from extractMetricEntries ({time, momentTime, value})
 * @param {string} interval - '15min' | '1hour' | 'daily'
 * @returns {Array} sorted array of { time: string, value: number }
 */
export const aggregateLatestIntoBuckets = (entries, interval) => {
    if (!entries || !entries.length) return [];

    const buckets = {};
    entries.forEach(e => {
        const m = e.momentTime;
        if (!m || !m.isValid()) return;

        let bucketKey;
        if (interval === '15min') {
            const minute = Math.floor(m.minute() / 15) * 15;
            bucketKey = m.clone().minute(minute).second(0).format('DD-MM-YYYY HH:mm');
        } else if (interval === '1hour') {
            bucketKey = m.clone().minute(0).second(0).format('DD-MM-YYYY HH:mm');
        } else if (interval === 'daily') {
            bucketKey = m.clone().startOf('day').format('DD-MM-YYYY');
        }

        // Guard: an unrecognised interval leaves bucketKey undefined, which
        // would pile every entry under buckets[undefined] and emit a single
        // point with time:undefined (moment parse warning + blank axis label).
        if (!bucketKey) return;

        // entries are sorted ascending, so the last write per bucket is latest.
        buckets[bucketKey] = { time: bucketKey, value: e.value };
    });

    const format = interval === 'daily' ? 'DD-MM-YYYY' : 'DD-MM-YYYY HH:mm';
    return Object.values(buckets).sort((a, b) =>
        moment(a.time, format).valueOf() - moment(b.time, format).valueOf()
    );
};

/**
 * Get valid interval options based on the selected time range.
 * Prevents useless single-bucket views (e.g. "daily" for "today").
 * @param {string} timeRange - 'today' | 'yesterday' | 'weekly' | 'daily_average'
 * @returns {Array} array of { value, label, disabled }
 */
export const getIntervalOptions = (timeRange) => {
    const allOptions = [
        { value: '15min', label: '15 min' },
        { value: '1hour', label: '1 hour' },
        { value: 'daily', label: 'Daily' },
    ];

    switch (timeRange) {
        case 'today':
        case 'yesterday':
            // Only 15min and 1hour make sense for a single day
            return allOptions.map(opt => ({
                ...opt,
                disabled: opt.value === 'daily'
            }));
        case 'weekly':
            // 1hour and daily make sense for a week
            return allOptions.map(opt => ({
                ...opt,
                disabled: opt.value === '15min'
            }));
        case 'daily_average':
            // Only daily makes sense for 30 days
            return allOptions.map(opt => ({
                ...opt,
                disabled: opt.value !== 'daily'
            }));
        default:
            return allOptions.map(opt => ({ ...opt, disabled: false }));
    }
};

/**
 * Calculate the date range for a given door time range selection.
 * Uses consistent logic for both frontend display and data fetching.
 * @param {string} range - 'today' | 'yesterday' | 'weekly' | 'daily_average'
 * @param {string} timezone - IANA timezone string
 * @returns {{ start: Moment, end: Moment, interval: string }}
 */
export const getDoorDateRange = (range, timezone) => {
    const tz = timezone;

    switch (range) {
        case 'today':
            return {
                start: moment().tz(tz).startOf('day'),
                end: moment().tz(tz),
                interval: '15min',
            };
        case 'yesterday':
            return {
                start: moment().tz(tz).subtract(1, 'day').startOf('day'),
                end: moment().tz(tz).subtract(1, 'day').endOf('day'),
                interval: '15min',
            };
        case 'weekly': {
            // Subtract day-of-week to get to Sunday (day 0)
            const dayOfWeek = moment().tz(tz).day();
            return {
                start: moment().tz(tz).subtract(dayOfWeek, 'days').startOf('day'),
                end: moment().tz(tz),
                interval: '1hour',
            };
        }
        case 'daily_average':
            return {
                start: moment().tz(tz).subtract(30, 'days').startOf('day'),
                end: moment().tz(tz),
                interval: 'daily',
            };
        default:
            return {
                start: moment().tz(tz).startOf('day'),
                end: moment().tz(tz),
                interval: '15min',
            };
    }
};
